import { fetchAddressGeom } from '@base/lib/fetchAddressGeom'
import { Ref, ref } from 'vue'
import { LocationQueryValue } from 'vue-router'

import { StateField, StateFieldConfig } from '../StateField'

export type AddressStateFieldConfig = StateFieldConfig<AddressStateField> & {
  type: 'address'
  default?: {
    addressLine1?: string
    addressLine2?: string
    city?: string
    province?: string
    postalCode?: string
    country?: string
  }
  overrideValue?: {
    addressLine1?: Ref<string>
    addressLine2?: Ref<string>
    city?: Ref<string>
    province?: Ref<string>
    postalCode?: Ref<string>
    country?: Ref<string>
  }

  // inputPlaceholder?: {
  //   addressLine1?: string
  //   addressLine2?: string
  //   city?: string
  //   province?: string
  //   postalCode?: string
  //   country?: string
  // }
  inputRequired?: boolean
}

export class AddressStateField extends StateField {
  readonly type = 'address'
  default?: {
    addressLine1?: string
    addressLine2?: string
    city?: string
    province?: string
    postalCode?: string
    country?: string
  }
  value: {
    addressLine1: Ref<string>
    addressLine2: Ref<string>
    city: Ref<string>
    province: Ref<string>
    postalCode: Ref<string>
    country: Ref<string>
  }

  inputPlaceholder?: {
    addressLine1?: string
    addressLine2?: string
    city?: string
    province?: string
    postalCode?: string
    country?: string
  }
  inputRequired?: boolean
  errors = {
    addressLine1: ref(''),
    addressLine2: ref(''),
    city: ref(''),
    province: ref(''),
    postalCode: ref(''),
    country: ref('')
  }

  constructor(config: AddressStateFieldConfig) {
    super(config)

    this.value = {
      addressLine1:
        config.overrideValue?.addressLine1 ||
        ref(config.default?.addressLine1 || ''),
      addressLine2:
        config.overrideValue?.addressLine2 ||
        ref(config.default?.addressLine2 || ''),
      city: config.overrideValue?.city || ref(config.default?.city || ''),
      province:
        config.overrideValue?.province || ref(config.default?.province || ''),
      postalCode:
        config.overrideValue?.postalCode ||
        ref(config.default?.postalCode || ''),
      country:
        config.overrideValue?.country || ref(config.default?.country || '')
    }
    this.default = config.default

    // this.inputPlaceholder = config.inputPlaceholder
    this.inputRequired = config.inputRequired
  }

  toRouteValue(): string | undefined {
    if (this.toRouteValueOverride) return this.toRouteValueOverride(this)

    return undefined
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  pullRouteQueryValue(value: LocationQueryValue | LocationQueryValue[]): void {
    if (this.pullRouteQueryValueOverride)
      return this.pullRouteQueryValueOverride(this, value)

    return
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  pullRouteParamValue(value: string | string[]): void {
    if (this.pullRouteParamValueOverride)
      return this.pullRouteParamValueOverride(this, value)

    return
  }

  async toRequestVariable(
    variation: number
  ): Promise<string | { lat: number; lon: number } | null | undefined> {
    if (this.toRequestVariableOverride)
      return await this.toRequestVariableOverride(this, variation)

    if (variation === 0) {
      return this.value.addressLine1.value
    }
    if (variation === 1) {
      return this.value.addressLine2.value
    }
    if (variation === 2) {
      return this.value.city.value
    }
    if (variation === 3) {
      return this.value.province.value
    }
    if (variation === 4) {
      return this.value.postalCode.value
    }
    if (variation === 5) {
      return this.value.country.value
    }
    if (variation === 6 && this.updated.value === true) {
      let addressGeom: { lat: number; lon: number } | null = null

      const addressLine1 = this.value.addressLine1.value
      const addressLine2 = this.value.addressLine2.value
      const city = this.value.city.value
      const province = this.value.province.value
      const postalCode = this.value.postalCode.value
      const country = this.value.country.value

      const addressString = `${addressLine1} ${addressLine2} ${city} ${province} ${postalCode} ${country}`
      addressGeom = await fetchAddressGeom(
        import.meta.env.VITE_GCP_GEOCODING_API_KEY,
        addressString
      )

      return addressGeom
    }
    return undefined
  }

  resetValue(): void {
    if (this.resetValueOverride) return this.resetValueOverride(this)

    this.value.addressLine1.value = this.default?.addressLine1 || ''
    this.value.addressLine2.value = this.default?.addressLine2 || ''
    this.value.city.value = this.default?.city || ''
    this.value.province.value = this.default?.province || ''
    this.value.postalCode.value = this.default?.postalCode || ''
    this.value.country.value = this.default?.country || ''
  }

  resetError(): void {
    this.errors.addressLine1.value = ''
    this.errors.addressLine2.value = ''
    this.errors.city.value = ''
    this.errors.province.value = ''
    this.errors.postalCode.value = ''
    this.errors.country.value = ''
  }
}
